import { useMutation, useReactiveVar } from '@apollo/client';
import Cached from '@mui/icons-material/Cached';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
} from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { cleanObjectFromUndefined, formatYearMonthDay } from '../../helpers';
import { getDefaultFiltersFromHistory, getFilterByNameFromHistory, historyToFilters, getAndEraseFiltersFromHistory } from '../../helpers/url-filter-translator';
import { useAvailableHeight, useExportDocument, useFilteredQuery, useIsMobile, useSelectedCompany, useTablePagination } from '../../hooks';
import { useAdditionalQueries } from '../../hooks/useAdditionalQueries';
import { ShoppingCartPreview } from '../banners';
import { DownloadExcelIconButton } from '../buttons';
import { FingoDataGrid } from '../dataGrids';
import { DocumentFilters } from '../filters';
import FirstLoadMessage from '../dataGrids/FirstLoadMessage';
import { PaperHeader } from '../headers';
import { preofferSelected } from '../../apollo/reactive-variables/invoices';
import { shoppingCartShow } from '../../apollo/reactive-variables/shopping-cart';
import { getInitialCompanyFilterVariables } from '../filters/selectedCompanyFilter';
import currencyVar from '../../apollo/reactive-variables/money';
import DataGridNoRowsOverlay from '../dataGrids/DataGridNoRowsOverlay';
import useGetCountryFromUrl from '../../hooks/useGetCountryFromUrl';
import CHANGE_COMPANY from '../../graphql/mutations/change-company';
import EXPORT_INVOICES from '../../graphql/queries/export-invoices';
import { DEFAULT_DATA_GRID_CLASS_NAME } from '../../theme/constants/data-grid';

const DocumentList = (
  {
    // Basic identification and configuration props
    type,
    headerTitle,
    queryDocument,
    queryExport,
    exportType,
    skipQueryDocument,

    // Visual and styling related props
    showGoBack,
    helpVideo,
    extraHeaderComponent,
    currency: propCurrency,

    // DataGrid configuration props
    preColumns,
    checkboxSelection,
    isRowSelectable,
    disableSelectionOnClick,
    disabledCheckboxTooltip,
    disabledRowTooltip,
    selectionModel,
    includeHeaders,
    mobileHeaders,
    initialPageSize,
    initialOrderBy,
    serverFilters,
    FirstLoadMessageComponent,
    DataGridNoRowsOverlayComponent,

    // DataGrid styling props
    dataGridClassName,
    mobileDensity,
    getRowClassName,
    gridMinHeight,

    // Filters and states props
    emitter,
    showFilters,
    showExportInvoice,
    financingStatusOptions,
    allCompaniesOptions,
    countryFilter,
    customVariables,
    initialInvoiceFilters,
    defaultFilterProps,

    // Shopping cart and summary functionality props
    shoppingCartPreview,
    customSummaryElement,
    showConfirmationSummary,
    setShowConfirmationSummary,
    ConfirmationSummary,

    // Callbacks and functions props
    flexEndButtons,
    onCompletedSetLength,
    onCompletedSetRows,
    readData,

    // Custom header props
    CustomHeader,
    customHeaderProps,

    // State and update props
    updateTrigger,
    setUpdateTrigger,
    updateCustomVariables,
    setUpdateCustomVariables,
    firstLoad,
    newData,
    useNewDataButton,
    openJoyride,

    // Additional queries functionality
    additionalQueries,
    dependentAdditionalQueries,
    getDataForDependentQueryMethods,

    ...restDataGridProps
  },
) => {
  const history = useHistory();
  const {
    defaultPageSize,
    defaultPage,
    defaultDates,
    defaultGlobalFilter,
    defaultOrderBy,
    defaultFilters,
  } = getDefaultFiltersFromHistory(history, initialOrderBy, initialInvoiceFilters);
  const [height, refChange] = useAvailableHeight();
  const [globalFilter, _setGlobalFilter] = useState(defaultGlobalFilter);
  const [invoiceFilters, _setInvoiceFilters] = useState({
    ...defaultFilters, ...initialInvoiceFilters,
  });
  const [customVariablesStates, setCustomVariablesStates] = useState(() => ({
    ...getInitialCompanyFilterVariables(defaultFilterProps),
  }));
  const [changeCompany] = useMutation(CHANGE_COMPANY, {
    onCompleted: () => shoppingCartShow(true) });
  const [openStateFilter, setOpenStateFilter] = useState(false);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [dates, setDates] = useState(defaultDates);
  const selectedCompany = useSelectedCompany();
  const country = useGetCountryFromUrl();
  const currencyVariable = useReactiveVar(currencyVar);
  const currency = propCurrency || currencyVariable;

  const paginationFiltersKey = JSON.stringify({
    selectedCompanyId: selectedCompany?.id ?? null,
    ...cleanObjectFromUndefined({
      ...invoiceFilters,
      globalFilter,
      orderBy,
      dateIssued_Gte: formatYearMonthDay(dates.dateIssued_Gte),
      dateIssued_Lte: formatYearMonthDay(dates.dateIssued_Lte),
      currency,
      ...customVariables,
      ...customVariablesStates,
    }),
  });

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
  } = useTablePagination({
    initialPage: defaultPage || 0,
    initialPageSize: initialPageSize || defaultPageSize,
    resetKey: paginationFiltersKey,
    skipReset: !country?.id,
  });

  const setGlobalFilter = useCallback((value) => {
    setPage(0);
    _setGlobalFilter(value);
  }, [setPage]);

  const baseVariables = {
    first: pageSize,
    offset: page * pageSize,
    globalFilter,
    orderBy,
    receiverId: !emitter && !invoiceFilters.allCompanies ? selectedCompany?.id : null,
    companyId: !!emitter && !invoiceFilters.allCompanies ? selectedCompany?.id : getFilterByNameFromHistory(history, 'companyId'),
    allReceiverCompany: !emitter ? Boolean(invoiceFilters.allCompanies) : null,
    allIssuedCompany: emitter ? Boolean(invoiceFilters.allCompanies) : null,
    dateIssued_Gte: formatYearMonthDay(dates.dateIssued_Gte),
    dateIssued_Lte: formatYearMonthDay(dates.dateIssued_Lte),
    loanedStatus: invoiceFilters.loanedStatus,
    creditNoteStatus: invoiceFilters.creditNoteStatus,
    siiStatus: invoiceFilters.siiStatus,
    dteType_Code_In: invoiceFilters.documentType,
    dteType_CountryId: countryFilter ? country?.id : null,
    dfstateIn: invoiceFilters.documentFinanceStatus,
    currency,
  };

  const commonVars = cleanObjectFromUndefined({
    ...invoiceFilters,
    ...baseVariables,
    ...customVariables,
    ...customVariablesStates,
  });
  const {
    data: rawData,
    loading,
    refetch: refetchDocuments,
    deletePaginatedAndRefetch,
  } = useFilteredQuery(
    queryDocument,
    {
      variables: commonVars,
      notifyOnNetworkStatusChange: true,
      skip: skipQueryDocument,
    },
  );
  const setInvoiceFilters = useCallback(
    (newState) => {
      if (typeof newState === 'object') {
        _setInvoiceFilters({ ...newState, currency });
      } else {
        _setInvoiceFilters({ ...newState(), currency });
      }
      historyToFilters(history, { ...commonVars, ...newState, currency });
    },
    [historyToFilters, history, commonVars],
  );

  const readDataAndRefresh = useCallback(() => {
    deletePaginatedAndRefetch();
    readData();
  }, [readData, deletePaginatedAndRefetch]);
  const newUpdateDocuments = useCallback(() => {
    readDataAndRefresh();
  }, [readDataAndRefresh]);
  const data = rawData ? onCompletedSetRows(rawData) : [];
  const rowCount = rawData ? onCompletedSetLength(rawData) : 0;

  const additionalQueriesResults = useAdditionalQueries({
    additionalQueries,
    dependentAdditionalQueries,
    commonVars,
    data,
    getDataForDependentQueryMethods,
  });

  useEffect(() => {
    const { changeCompanyTo, preoffer } = getAndEraseFiltersFromHistory(history, 'changeCompanyTo', 'preoffer');
    historyToFilters(history, commonVars);
    if (changeCompanyTo) {
      changeCompany({ variables: { companyId: changeCompanyTo } });
      preofferSelected([preoffer]);
    }
  }, []);

  useEffect(() => {
    if (updateTrigger || (currency && currency !== '')) {
      refetchDocuments({ ...commonVars, currency });
      setUpdateTrigger(false);
    }
  }, [updateTrigger, currency]);

  useEffect(() => {
    if (updateCustomVariables) {
      setUpdateCustomVariables(false);
      setCustomVariablesStates({
        ...getInitialCompanyFilterVariables(defaultFilterProps),
        currency,
      });
    }
  }, [updateCustomVariables]);

  useEffect(() => {
    if (firstLoad && newData) {
      readDataAndRefresh();
    }
  }, [firstLoad, newData]);

  const {
    exportDocuments: exportInvoices,
    loading: exportingDocuments,
  } = useExportDocument(queryExport, { ...commonVars, rowCount, exportType });

  const columns = preColumns.map((col) => {
    if (col.type === 'number') {
      return {
        ...col,
        filterOperators: getGridNumericOperators().filter(
          (operator) => ['=', '>', '>=', '<', '<='].includes(operator.value),
        ),
      };
    }
    if (col.type === 'date') {
      return {
        ...col,
        filterOperators: getGridDateOperators().filter(
          (operator) => ['is', 'after', 'onOrAfter', 'before', 'onOrBefore'].includes(operator.value),
        ),
      };
    }
    if (col.field === 'company_MasterEntity_Name' || col.field === 'receiver_Name') {
      return {
        ...col,
        filterOperators: getGridStringOperators().filter(
          (operator) => ['equals', 'contains', 'startsWith'].includes(operator.value),
        ),
      };
    }
    if (col.type === 'string') {
      return {
        ...col,
        filterOperators: getGridStringOperators().filter(
          (operator) => ['equals', 'isAnyOf'].includes(operator.value),
        ),
      };
    }
    if (col.type === 'singleSelect') {
      return {
        ...col,
        filterOperators: getGridSingleSelectOperators().filter(
          (operator) => ['is', 'isAnyOf'].includes(operator.value),
        ),
      };
    }
    return col;
  });
  const isMobile = useIsMobile();
  const flexEndButtonsItem = useMemo(() => {
    if (showExportInvoice && !isMobile) {
      return (
        <DownloadExcelIconButton
          downloadFunction={exportInvoices}
          loading={exportingDocuments}
        />
      );
    }
    return null;
  }, [showExportInvoice, exportInvoices, exportingDocuments, isMobile]);

  const showShoppingCartMemo = useMemo(
    () => shoppingCartPreview && selectionModel.length !== 0,
    [shoppingCartPreview, selectionModel],
  );
  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {CustomHeader ? (
        <CustomHeader
          headerTitle={headerTitle}
          filters={commonVars}
          financingStatusOptions={financingStatusOptions}
          allCompaniesOptions={allCompaniesOptions}
          states={invoiceFilters}
          setStates={setInvoiceFilters}
          dates={dates}
          setDates={setDates}
          setCustomVariables={setCustomVariablesStates}
          finder={{
            onFinderChange: setGlobalFilter,
            searchPlaceHolder: 'Búsqueda por folio, rut o nombres',
            finderValue: globalFilter,
            canPasteExcel: true,
            setColumnFilter: setInvoiceFilters,
          }}
          defaultFilterProps={defaultFilterProps}
          onRefresh={newUpdateDocuments}
          refreshLoading={loading}
          {...customHeaderProps}
          cartType={type}
          selectionModel={selectionModel}
          setShowConfirmationSummary={setShowConfirmationSummary}
          showConfirmationSummary={showConfirmationSummary}
        />
      ) : (
        <PaperHeader
          type={type}
          viewTitle={headerTitle}
          finder={{
            onFinderChange: setGlobalFilter,
            searchPlaceHolder: 'Búsqueda por folio, rut o nombres',
            finderValue: globalFilter,
            canPasteExcel: true,
            setColumnFilter: setInvoiceFilters,
          }}
          helpVideo={helpVideo}
          openJoyride={openJoyride}
          showGoBack={showGoBack}
          intermediateComponent={extraHeaderComponent}
        />
      )}
      {showShoppingCartMemo && <ShoppingCartPreview cartType={type} />}
      {showFilters && (
        <DocumentFilters
          type={type}
          dates={dates}
          setDates={setDates}
          financingStatusOptions={financingStatusOptions}
          allCompaniesOptions={allCompaniesOptions}
          states={invoiceFilters}
          setStates={setInvoiceFilters}
          setCustomVariables={setCustomVariablesStates}
          customVariables={customVariablesStates}
          flexEndButtons={() => flexEndButtons(rowCount, commonVars)}
          canPasteExcel
          showRefresh
          onRefresh={newUpdateDocuments}
          loading={loading}
          openStateFilter={openStateFilter}
          setOpenStateFilter={setOpenStateFilter}
          showExportInvoice
          showExportExcelButton
          exportExcelIconButton={flexEndButtonsItem}
          {...defaultFilterProps}
        />
      )}
      {customSummaryElement}
      {!!(newData && !firstLoad && useNewDataButton && !showConfirmationSummary) && (
        <Box display="flex" justifyContent="center" top={55} position="relative" width="100%" height={0}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            endIcon={<Cached />}
            onClick={readDataAndRefresh}
            sx={{
              height: 25,
              zIndex: 100,
            }}
          >
            Tienes {newData} documentos nuevos
          </Button>
        </Box>
      )}
      {showConfirmationSummary ? (
        ConfirmationSummary
      ) : (
        <Box
          ref={refChange}
          sx={{
            height,
            width: '100%',
            minHeight: !rowCount ? gridMinHeight : 0,
          }}
        >
          <FingoDataGrid
            dataGridClassName={dataGridClassName}
            serverFilters={serverFilters}
            loadingWithSkeleton={loading}
            rows={data}
            columns={columns}
            includeHeaders={includeHeaders}
            mobileHeaders={mobileHeaders}
            rowCount={rowCount}
            initialState={{
              sorting: {
                sortModel: [{ field: 'dateIssued', sort: 'desc' }],
              },
            }}
            pageSize={pageSize}
            disableSelectionOnClick={disableSelectionOnClick}
            setColumnFilter={setInvoiceFilters}
            setOrderBy={setOrderBy}
            orderBy={orderBy}
            page={page}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            checkboxSelection={checkboxSelection}
            disabledCheckboxTooltip={disabledCheckboxTooltip}
            disabledRowTooltip={disabledRowTooltip}
            keepNonExistentRowsSelected={checkboxSelection && serverFilters}
            isRowSelectable={isRowSelectable}
            selectionModel={selectionModel}
            loading={loading}
            mobileDensity={mobileDensity}
            getRowClassName={getRowClassName}
            noRowsMessage={firstLoad ? FirstLoadMessage : null}
            DataGridNoRowsOverlayComponent={firstLoad
              ? DataGridNoRowsOverlay : DataGridNoRowsOverlayComponent}
            additionalQueriesResults={additionalQueriesResults}
            {...restDataGridProps}
          />
        </Box>
      )}
    </Box>
  );
};

DocumentList.propTypes = {
  type: PropTypes.string.isRequired,
  headerTitle: PropTypes.string,
  queryDocument: PropTypes.shape().isRequired,
  queryExport: PropTypes.shape(),
  exportType: PropTypes.string,
  skipQueryDocument: PropTypes.bool,
  dataGridClassName: PropTypes.string,
  mobileDensity: PropTypes.string,
  showGoBack: PropTypes.bool,
  helpVideo: PropTypes.string,
  extraHeaderComponent: PropTypes.element,
  currency: PropTypes.string,
  preColumns: PropTypes.arrayOf(PropTypes.shape()),
  checkboxSelection: PropTypes.bool,
  isRowSelectable: PropTypes.func,
  disableSelectionOnClick: PropTypes.bool,
  disabledCheckboxTooltip: PropTypes.string,
  disabledRowTooltip: PropTypes.func,
  selectionModel: PropTypes.arrayOf(PropTypes.string),
  includeHeaders: PropTypes.arrayOf(PropTypes.string),
  mobileHeaders: PropTypes.arrayOf(PropTypes.string),
  initialPageSize: PropTypes.number,
  initialOrderBy: PropTypes.string,
  serverFilters: PropTypes.bool,
  emitter: PropTypes.bool,
  showFilters: PropTypes.bool,
  showExportInvoice: PropTypes.bool,
  financingStatusOptions: PropTypes.bool,
  allCompaniesOptions: PropTypes.bool,
  countryFilter: PropTypes.bool,
  customVariables: PropTypes.shape(),
  initialInvoiceFilters: PropTypes.shape(),
  defaultFilterProps: PropTypes.shape({
    showPreoffersFilters: PropTypes.bool,
    showQuickFilters: PropTypes.bool,
    showValidSiiCredentialsFilter: PropTypes.bool,
    showLightningFilter: PropTypes.bool,
    showStatesFilter: PropTypes.bool,
    showDatesFilter: PropTypes.bool,
    showRefresh: PropTypes.bool,
    defaultFilterDays: PropTypes.number,
    showCompanyIssuerFilter: PropTypes.bool,
    showHuntingFarmingFilter: PropTypes.bool,
    showBooleanSwitchFilter: PropTypes.bool,
    booleanSwitchFilterConfig: PropTypes.shape({
      label: PropTypes.string,
      checkedKey: PropTypes.string,
      checkedValue: PropTypes.bool,
      onCustomVariables: PropTypes.shape(),
      offCustomVariables: PropTypes.shape(),
    }),
    showInvoiceStatusFilters: PropTypes.bool,
    showExecutiveFilter: PropTypes.bool,
  }),
  shoppingCartPreview: PropTypes.bool,
  customSummaryElement: PropTypes.element,
  showConfirmationSummary: PropTypes.bool,
  setShowConfirmationSummary: PropTypes.func,
  ConfirmationSummary: PropTypes.element,
  flexEndButtons: PropTypes.func,
  onCompletedSetLength: PropTypes.func,
  onCompletedSetRows: PropTypes.func,
  readData: PropTypes.func,
  getRowClassName: PropTypes.func,
  CustomHeader: PropTypes.func,
  customHeaderProps: PropTypes.shape(),
  updateTrigger: PropTypes.bool,
  setUpdateTrigger: PropTypes.func,
  updateCustomVariables: PropTypes.bool,
  setUpdateCustomVariables: PropTypes.func,
  firstLoad: PropTypes.bool,
  newData: PropTypes.number,
  openJoyride: PropTypes.func,
  FirstLoadMessageComponent: PropTypes.elementType,
  DataGridNoRowsOverlayComponent: PropTypes.elementType,
  useNewDataButton: PropTypes.bool,
  additionalQueries: PropTypes.objectOf(
    PropTypes.shape({
      query: PropTypes.shape({}).isRequired,
      variables: PropTypes.shape({}),
      skip: PropTypes.bool,
      queryCustomVariables: PropTypes.shape(),
    }),
  ),
  dependentAdditionalQueries: PropTypes.objectOf(
    PropTypes.shape({
      query: PropTypes.shape({}).isRequired,
      variables: PropTypes.shape({}),
      skip: PropTypes.bool,
      queryCustomVariables: PropTypes.shape(),
    }),
  ),
  getDataForDependentQueryMethods: PropTypes.objectOf(PropTypes.func),
  gridMinHeight: PropTypes.number,
};

DocumentList.defaultProps = {
  headerTitle: '',
  checkboxSelection: false,
  emitter: false,
  countryFilter: false,
  isRowSelectable: () => true,
  queryExport: EXPORT_INVOICES,
  includeHeaders: [],
  preColumns: [],
  updateTrigger: false,
  setUpdateTrigger: () => null,
  serverFilters: true,
  mobileHeaders: [],
  showExportInvoice: false,
  financingStatusOptions: false,
  allCompaniesOptions: false,
  customVariables: {},
  showGoBack: false,
  skipQueryDocument: undefined,
  initialInvoiceFilters: {},
  showFilters: false,
  defaultFilterProps: {
    showValidSiiCredentialsFilter: false,
    showLightningFilter: false,
    showPreoffersFilters: false,
    showStatesFilter: true,
    showDatesFilter: true,
    showRefresh: true,
    defaultFilterDays: 3,
    showCompanyIssuerFilter: false,
    showHuntingFarmingFilter: false,
    showBooleanSwitchFilter: false,
    booleanSwitchFilterConfig: {},
    showQuickFilters: false,
    showInvoiceStatusFilters: false,
    buttonsToShow: [],
    showExecutiveFilter: false,
  },
  shoppingCartPreview: false,
  customSummaryElement: null,
  flexEndButtons: () => null,
  onCompletedSetLength: (data) => data.invoices.totalCount,
  onCompletedSetRows: (data) => data.invoices.edges.map((edge) => (edge.node)),
  initialOrderBy: '-dateIssued',
  helpVideo: undefined,
  openJoyride: undefined,
  CustomHeader: undefined,
  newData: 0,
  readData: () => {},
  selectionModel: [],
  customHeaderProps: {},
  firstLoad: false,
  initialPageSize: null,
  disabledCheckboxTooltip: null,
  disabledRowTooltip: undefined,
  updateCustomVariables: false,
  exportType: 'lending',
  DataGridNoRowsOverlayComponent: DataGridNoRowsOverlay,
  FirstLoadMessageComponent: FirstLoadMessage,
  setUpdateCustomVariables: () => {},
  extraHeaderComponent: null,
  currency: '',
  dataGridClassName: DEFAULT_DATA_GRID_CLASS_NAME,
  disableSelectionOnClick: true,
  mobileDensity: 'standard',
  showConfirmationSummary: false,
  setShowConfirmationSummary: () => {},
  ConfirmationSummary: undefined,
  getRowClassName: () => '',
  useNewDataButton: false,
  additionalQueries: {},
  dependentAdditionalQueries: {},
  getDataForDependentQueryMethods: {},
  gridMinHeight: 0,
};

export default DocumentList;
